import { useMemo } from 'react';
import {
  freezePresetIntoCustom,
  PRESETS,
  resolveAddons,
  resolveCostMultiplier,
  resolveSponsorFill,
  resolveTicketFill,
  SCENARIO_LABELS,
} from '../lib/scenarios';
import type {
  AddonsScenario,
  EventData,
  Scenario,
  ScenarioName,
} from '../lib/types';

type Props = {
  data: EventData;
  onChange: (s: Scenario) => void;
};

const PRESET_BUTTONS: ScenarioName[] = [
  'pessimistic',
  'realistic',
  'optimistic',
  'custom',
];

export default function ScenarioPanel({ data, onChange }: Props) {
  const s = data.scenario;
  const isCustom = s.active === 'custom';

  function selectPreset(name: ScenarioName) {
    if (name === 'custom') {
      // Drop in with the previous preset's values so the user has a
      // sensible starting point to nudge.
      const seed =
        s.active === 'custom'
          ? s.custom
          : freezePresetIntoCustom(data, s.active);
      onChange({ active: 'custom', custom: seed });
      return;
    }
    onChange({ active: name, custom: s.custom });
  }

  // When the user nudges any slider, switch to custom — seed with the
  // currently-effective rates so the slider starts at its visible value.
  function nudge(mutate: (custom: Scenario['custom']) => Scenario['custom']) {
    const seed =
      s.active === 'custom' ? s.custom : freezePresetIntoCustom(data, s.active);
    onChange({ active: 'custom', custom: mutate(seed) });
  }

  // Per-tier rates rendered in the Custom editor. Memoised so the slider
  // list isn't recomputed on unrelated re-renders.
  const ticketRates = useMemo(
    () => data.tickets.map((t) => ({ id: t.id, name: t.name, qty: t.qty, rate: resolveTicketFill(s, t.id) })),
    [data.tickets, s],
  );
  const sponsorRates = useMemo(
    () => data.sponsors.map((sp) => ({ id: sp.id, name: sp.name, qty: sp.qty, rate: resolveSponsorFill(s, sp.id) })),
    [data.sponsors, s],
  );
  const addonsMode = resolveAddons(s);
  const costMultiplier = resolveCostMultiplier(s);

  return (
    <div className="card scenario-card">
      <div className="section-head">
        <h2>Scenario</h2>
        <span className="muted">
          Pick a preset, or <strong>Custom</strong> to fine-tune per tier.
        </span>
      </div>

      <div className="scenario-presets">
        {PRESET_BUTTONS.map((name) => (
          <button
            key={name}
            type="button"
            className={`scenario-preset ${s.active === name ? 'active' : ''}`}
            onClick={() => selectPreset(name)}
          >
            {SCENARIO_LABELS[name]}
            {name !== 'custom' && (
              <span className="scenario-hint">
                tickets {Math.round(PRESETS[name].tickets * 100)}% · costs ×
                {PRESETS[name].costMultiplier.toFixed(2)}
              </span>
            )}
          </button>
        ))}
      </div>

      {isCustom && (
        <div className="scenario-detail">
          {ticketRates.length > 0 && (
            <FillRateGroup
              label="Ticket fill rate"
              rows={ticketRates.map((t) => ({
                id: t.id,
                name: t.name,
                planned: t.qty,
                rate: t.rate,
              }))}
              onRateChange={(id, rate) =>
                nudge((c) => ({ ...c, ticketFill: { ...c.ticketFill, [id]: rate } }))
              }
            />
          )}

          {sponsorRates.length > 0 && (
            <FillRateGroup
              label="Sponsor fill rate"
              rows={sponsorRates.map((sp) => ({
                id: sp.id,
                name: sp.name,
                planned: sp.qty,
                rate: sp.rate,
              }))}
              onRateChange={(id, rate) =>
                nudge((c) => ({ ...c, sponsorFill: { ...c.sponsorFill, [id]: rate } }))
              }
            />
          )}

          <div className="scenario-group">
            <div className="scenario-group-label">Add-ons</div>
            <div className="scenario-radio-group">
              {(['as-planned', 'all-on', 'all-off'] as AddonsScenario[]).map(
                (mode) => (
                  <label key={mode} className="scenario-radio">
                    <input
                      type="radio"
                      checked={addonsMode === mode}
                      onChange={() =>
                        nudge((c) => ({ ...c, addonsEnabled: mode }))
                      }
                    />
                    <span>{labelForAddons(mode)}</span>
                  </label>
                ),
              )}
            </div>
          </div>

          <div className="scenario-group">
            <div className="scenario-group-label">
              Cost multiplier
              <span className="muted ml-8">
                ×{costMultiplier.toFixed(2)} ·{' '}
                {Math.round((costMultiplier - 1) * 100) >= 0 ? '+' : ''}
                {Math.round((costMultiplier - 1) * 100)}% vs. plan
              </span>
            </div>
            <input
              type="range"
              min={0.5}
              max={2.0}
              step={0.05}
              value={costMultiplier}
              onChange={(e) =>
                nudge((c) => ({
                  ...c,
                  costMultiplier: Number(e.target.value),
                }))
              }
            />
          </div>

        </div>
      )}
    </div>
  );
}

function FillRateGroup({
  label,
  rows,
  onRateChange,
}: {
  label: string;
  rows: { id: string; name: string; planned: number; rate: number }[];
  onRateChange: (id: string, rate: number) => void;
}) {
  return (
    <div className="scenario-group">
      <div className="scenario-group-label">{label}</div>
      <div className="scenario-tier-rows">
        {rows.map((r) => {
          const projected = Math.round(r.planned * r.rate);
          return (
            <div key={r.id} className="scenario-tier-row">
              <div className="scenario-tier-name">{r.name}</div>
              <input
                type="range"
                min={0}
                max={1.5}
                step={0.05}
                value={r.rate}
                onChange={(e) =>
                  onRateChange(r.id, Number(e.target.value))
                }
              />
              <div className="scenario-tier-value">
                {Math.round(r.rate * 100)}%
                <span className="scenario-tier-sub">
                  {projected} / {r.planned}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function labelForAddons(m: AddonsScenario): string {
  switch (m) {
    case 'as-planned': return 'As planned';
    case 'all-on':     return 'All on';
    case 'all-off':    return 'All off';
  }
}
